import _ from "lodash";
import { useEffect, useState } from "react";

import { Team, useGetAllTeamsQuery, useGetTeamsByUserLeague, useGetUser } from "../../../../redux/slices";

export type TeamGroups = { [key: string | number]: Team[] };

export type GetGroupedLeagueTeams = {
  groups: TeamGroups;
  groupOrder: (string | number)[];
  playCountMap: Map<string, number>;
  loading: boolean;
};

export const useGetGroupedLeagueTeams = (): GetGroupedLeagueTeams => {
  const [groups, setGroups] = useState<TeamGroups>({});
  const [groupOrder, setGroupOrder] = useState<(string | number)[]>([]);
  const [playCountMap, setPlayCountMap] = useState<any>(new Map());
  const [loading, setLoading] = useState(true);
  const { data: teams, isLoading: loadingLeagueTeams } = useGetTeamsByUserLeague();
  const { data: teamsOrig, isLoading: loadingAllTeams } = useGetAllTeamsQuery();
  const { user } = useGetUser();

  useEffect(() => {
    const getLeagueGrouping = () => {
      // if every team has a conference, return "conference"
      const allHasConference = teams?.every((team: any) => team.conference);

      if (allHasConference) return "conference";

      return "none";
    };

    const getTeamGroupsAndOrder = () => {
      if (!teams || !teamsOrig) return { groups: {}, groupOrder: [], playCountMap: new Map(), customTeams: [] };

      const playCountMap = new Map(); // when teams api is cleaned up we won't need this
      teamsOrig?.forEach((team: any) => {
        playCountMap.set(team.team_id, team.noOfPlays);
      });

      const leagueGrouping = getLeagueGrouping();

      if (leagueGrouping === "none") {
        // break teams into groups of 10
        const teamChunks = _.chunk(teams, 10);
        const teamGroups: TeamGroups = {};
        teamChunks.forEach((chunk, index) => {
          teamGroups[index] = chunk;
        });

        // create an array with a number for each group
        const groupNumbers: (string | number)[] = Object.keys(teamGroups).map((key) => parseInt(key));

        return {
          groups: teamGroups,
          groupOrder: groupNumbers,
          playCountMap,
        };
      }

      const groups = _.groupBy(teams, leagueGrouping);

      const groupNameArray = Object.keys(groups);
      groupNameArray.sort(); // sort groups alphabetically
      const userTeam = teams.find((team) => team.id === user?.userDetails.teamId);

      if (!userTeam) return { groups, groupOrder: groupNameArray, playCountMap };

      const userGroup = userTeam[leagueGrouping];

      // move user's group to front of the groupNameArray
      const userGroupIndex = groupNameArray.indexOf(userGroup);
      const customIndex = groupNameArray.indexOf("Custom");
      if (userGroupIndex > -1) {
        if (customIndex > -1) {
          // move user group to the 2nd position
          groupNameArray.splice(userGroupIndex, 1);
          groupNameArray.splice(1, 0, userGroup);
        } else {
          groupNameArray.splice(userGroupIndex, 1);
          groupNameArray.unshift(userGroup);
        }
      }

      return { groups, groupOrder: groupNameArray, playCountMap };
    };

    if (loadingLeagueTeams || loadingAllTeams) {
      setLoading(true);
      return;
    }

    const { groups, groupOrder, playCountMap } = getTeamGroupsAndOrder();

    setGroups(groups);
    setGroupOrder(groupOrder);
    setPlayCountMap(playCountMap);
    setLoading(false);
  }, [loadingAllTeams, loadingLeagueTeams, teams, teamsOrig, user?.userDetails.teamId]);

  return { groups, groupOrder, playCountMap, loading };
};
